// Hooks for the admin "Clubs" tab: club settings, admin/pinned posts, club
// polls, the per-city member breakdown (which cities are ready for an
// in-person event), and the forum report queue.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { ClubAPI } from "@shared/types/api";

const KEY = ["admin", "clubs"] as const;

export function useAdminClubs() {
  return useQuery<ClubAPI.AdminClub[]>({
    queryKey: KEY,
    queryFn: async () => {
      const json = await api.get<ClubAPI.AdminClub[]>("/admin/clubs");
      return json.data ?? [];
    },
  });
}

export function useAdminClub(id: string | undefined) {
  return useQuery<ClubAPI.AdminClubDetail>({
    queryKey: [...KEY, "detail", id],
    enabled: !!id,
    queryFn: async () => {
      const json = await api.get<ClubAPI.AdminClubDetail>(`/admin/clubs/${id}`);
      if (!json.data) throw new Error(json.error || "Club not found");
      return json.data;
    },
  });
}

/** (club, city) pairs that reached the member threshold without an event — badge on the Clubs tab. */
export function useClubReadyCount(opts: { enabled?: boolean } = {}) {
  return useQuery<number>({
    queryKey: [...KEY, "ready-count"],
    enabled: opts.enabled ?? true,
    staleTime: 60_000,
    queryFn: async () => {
      const json = await api.get<{ count: number }>("/admin/clubs/ready-count");
      return json.data?.count ?? 0;
    },
  });
}

function useInvalidateClubs() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: KEY });
}

export function useCreateClub() {
  const invalidate = useInvalidateClubs();
  return useMutation({
    mutationFn: async (data: ClubAPI.CreateClubRequest) => {
      const json = await api.post<ClubAPI.Club>("/admin/clubs", data);
      if (!json.success || !json.data) throw new Error(json.error || "Failed to create club");
      return json.data;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateClub() {
  const invalidate = useInvalidateClubs();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ClubAPI.UpdateClubRequest }) => {
      const json = await api.patch<ClubAPI.Club>(`/admin/clubs/${id}`, data);
      if (!json.success) throw new Error(json.error || "Failed to update club");
      return json.data;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteClub() {
  const invalidate = useInvalidateClubs();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/clubs/${id}`),
    onSuccess: invalidate,
  });
}

export function useCreateAdminClubPost() {
  const invalidate = useInvalidateClubs();
  return useMutation({
    mutationFn: ({ clubId, data }: { clubId: string; data: ClubAPI.AdminCreatePostRequest }) =>
      api.post(`/admin/clubs/${clubId}/posts`, data),
    onSuccess: invalidate,
  });
}

/** Edit an admin post's title/body, or pin/unpin any post. */
export function useUpdateAdminClubPost() {
  const invalidate = useInvalidateClubs();
  return useMutation({
    mutationFn: ({ clubId, postId, data }: { clubId: string; postId: string; data: ClubAPI.AdminUpdatePostRequest }) =>
      api.patch(`/admin/clubs/${clubId}/posts/${postId}`, data),
    onSuccess: invalidate,
  });
}

export function useRemoveClubPost() {
  const invalidate = useInvalidateClubs();
  return useMutation({
    mutationFn: ({ clubId, postId }: { clubId: string; postId: string }) =>
      api.delete(`/admin/clubs/${clubId}/posts/${postId}`),
    onSuccess: invalidate,
  });
}

export function useDismissClubReport() {
  const invalidate = useInvalidateClubs();
  return useMutation({
    mutationFn: ({ clubId, reportId }: { clubId: string; reportId: string }) =>
      api.patch(`/admin/clubs/${clubId}/reports/${reportId}`, { status: "dismissed" }),
    onSuccess: invalidate,
  });
}
